import { CommonModule } from "@angular/common";
import { Component, CUSTOM_ELEMENTS_SCHEMA, OnInit } from "@angular/core";
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { STEP_ROUTE_MAP } from "@zp-config/step-route-map.config";
import { getTranslatedOptionsMap } from "@zp-core/i18n/form-i18n.utils";
import { Question } from "@zp-models/question";
import { StateService } from "@zp-services/state.service";
import { NesFormControlDirective } from "@zp-utils/directives/nes-form-control.directive";
import { map, Observable } from "rxjs";
import { QuestionStepperComponent } from "../question-stepper/question-stepper.component";

@Component({
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    NesFormControlDirective,
    QuestionStepperComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  selector: "app-question-step",
  templateUrl: "./question-step.component.html",
  styleUrls: ["./question-step.component.css"],
})
export class QuestionStepComponent implements OnInit {
  form: FormGroup;
  question: Question;
  questionId: string;

  noOfSteps: number;
  currentStepNumber: number;
  stepsConfig: Record<string, Question>;

  getTranslatedOptionsArray: Record<string, Observable<{ value: string; label: string }[]>> = {};

  flowIsComplete = false;

  constructor(
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private stateService: StateService,
    private router: Router,
    private translate: TranslateService
  ) {
    this.form = this.fb.group({});
  }

  ngOnInit(): void {
    this.stepsConfig = this.stateService.getQuestionWizardStepsConfig();
    this.noOfSteps = Object.keys(this.stepsConfig).length;

    this.route.data.subscribe((data) => {
      this.questionId = data["stepId"];
      this.question = this.stepsConfig[this.questionId];

      this.currentStepNumber = Object.keys(this.stepsConfig).indexOf(this.questionId) + 1;

      const savedData = this.stateService.getAnswer(this.questionId);
      this.buildForm(savedData);
    });

    this.flowIsComplete = this.stateService.areAllStepsAnswered();

    this.translateFieldOptions();
  }

  patchForm(savedData: any): void {
    Object.keys(savedData).forEach((key) => {
      const control = this.form.get(key);

      if (control instanceof FormArray && Array.isArray(savedData[key])) {
        control.clear();
        savedData[key].forEach((val: any) => {
          (control as FormArray).push(new FormControl(val));
        });
      } else if (control) {
        control.setValue(savedData[key]);
      }
    });
  }

  translateFieldOptions() {
    this.question.fields.forEach((field) => {
      if (["select", "radio"].includes(field.type)) {
        const baseObservable = getTranslatedOptionsMap(field, this.translate, this.questionId);

        if (field.type === "radio") {
          this.getTranslatedOptionsArray[field.name] = baseObservable.pipe(
            map((options) => {
              const selectedValue = this.form?.get(field.name)?.value;
              return options.map((opt) => ({
                ...opt,
                checked: selectedValue === opt.value,
              }));
            })
          );
        } else {
          this.getTranslatedOptionsArray[field.name] = baseObservable;
        }
      }
    });
  }

  buildForm(savedData?: any): void {
    const group: Record<string, any> = {};

    this.question.fields.forEach((field) => {
      const validators = [];
      if (field.required) {
        validators.push(Validators.required);
      }

      if (field.type === "checkbox") {
        group[field.name] = this.fb.array([], validators);
      } else {
        group[field.name] = new FormControl("", validators);
      }
    });

    this.form = this.fb.group(group);

    if (savedData) {
      this.patchForm(savedData);
    }
  }

  onSelectableCheckboxChange(event: Event): void {
    const customEvent = event as CustomEvent;
    const detail = customEvent.detail;

    if (!detail || typeof detail.value === "undefined") {
      console.warn("[SelectableCheckboxChange] Invalid event detail:", detail);
      return;
    }

    const selectedValue = detail.value;

    const matchingField = this.question.fields.find(
      (field) =>
        field.type === "checkbox" &&
        Array.isArray(field.options) &&
        field.options.some((opt) => opt.value === selectedValue)
    );

    if (!matchingField) {
      console.warn(
        "[SelectableCheckboxChange] No matching checkbox field found for value:",
        selectedValue
      );
      return;
    }

    const formArray = this.form.get(matchingField.name) as FormArray;
    if (!(formArray instanceof FormArray)) {
      console.warn("[Expected FormArray] Got:", formArray);
      return;
    }

    const index = formArray.controls.findIndex((ctrl) => ctrl.value === selectedValue);

    if (index === -1) {
      formArray.push(new FormControl(selectedValue));
    } else {
      formArray.removeAt(index);
    }

    formArray.markAsTouched();
  }

  onSelectableRadioChange(event: CustomEvent): void {
    const selectedValue = event.detail?.value;

    if (!selectedValue) return;

    const matchingField = this.question.fields.find(
      (field) => field.type === "radio" && field.options?.some((opt) => opt.value === selectedValue)
    );

    if (matchingField) {
      this.form.get(matchingField.name)?.setValue(selectedValue);
      this.form.get(matchingField.name)?.markAsTouched();
    } else {
      console.warn("[SelectableChange] No matching field found for value:", selectedValue);
    }
  }

  onSubmit(): void {
    this.form.markAllAsTouched();

    if (this.form.invalid) {
      console.warn("[Form Submission] Form is invalid. Please fill in all required fields.");
      return;
    }

    this.stateService.setStepData(this.questionId, this.form.value);

    const nextStepIdentifier = this.stateService.getNextStep(this.questionId);

    if (nextStepIdentifier) {
      if (nextStepIdentifier === "advies") {
        this.router.navigate(["/advies"]);
      } else if (nextStepIdentifier === "afronden") {
        this.router.navigate(["/aanvragen/afronden"]);
      } else {
        const nextRoutePath = STEP_ROUTE_MAP[nextStepIdentifier];

        this.router.navigate([nextRoutePath], {
          relativeTo: this.route.parent,
        });
      }
    } else {
      console.warn(
        `[Form Submission] Reached end of question wizard. No next step defined in wizard.`
      );
    }
  }

  hasError(fieldName: string, errorType: string): boolean | null {
    const control = this.form.get(fieldName);
    return control && control.hasError(errorType) && (control.dirty || control.touched);
  }
}
